import { useEffect, useMemo, useState } from "react";
import countryList from "react-select-country-list";
import { CircleLoader } from "../CircleLoader";
import {
  MapPin,
  Phone,
  Heart,
  Camera,
  ChevronLeft,
  UploadCloud,
} from "lucide-react";
import { uploadAccept } from "../../utilities/importantFns";

export default function Second({
  prevStep,
  handleChange,
  handleSubmit,
  form,
  loading,
}: any) {
  const options = useMemo(() => countryList().getData(), []);
  const [profilePreview, setProfilePreview] = useState("");

  useEffect(() => {
    if (!form.profile_picture) {
      setProfilePreview("");
      return;
    }

    if (form.profile_picture instanceof Blob) {
      const previewUrl = URL.createObjectURL(form.profile_picture);
      setProfilePreview(previewUrl);
      return () => URL.revokeObjectURL(previewUrl);
    }

    if (typeof form.profile_picture === "string") {
      setProfilePreview(form.profile_picture);
    }
  }, [form.profile_picture]);

  const inputClass =
    "w-full h-12 px-4 bg-slate-50 border-2 border-transparent rounded-xl focus:border-blue-500 focus:bg-white outline-none transition-all text-slate-700 font-medium placeholder:text-slate-400";
  const labelClass =
    "text-xs font-black text-slate-500 uppercase tracking-widest mb-2 block ml-1";

  return (
    <form onSubmit={handleSubmit}>
      <div className="p-8 md:p-12 animate-in fade-in slide-in-from-right-4 duration-500">
        <div className="flex flex-col lg:flex-row gap-12">
          <div className="flex-1 space-y-8">
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-amber-50 text-amber-600 rounded-2xl">
                  <MapPin size={24} />
                </div>
                <h2 className="text-xl font-black text-slate-800 tracking-tight">
                  Residential Address
                </h2>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <div className="space-y-2">
                  <label className={labelClass}>
                    Street Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="house_address"
                    value={form.house_address}
                    onChange={handleChange}
                    placeholder="House Number & Street"
                    className={inputClass}
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    placeholder="City*"
                    className={inputClass}
                    required
                  />
                  <input
                    type="text"
                    name="state"
                    value={form.state}
                    onChange={handleChange}
                    placeholder="State*"
                    className={inputClass}
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select
                    name="country"
                    onChange={handleChange}
                    value={form.country}
                    className={inputClass}
                    required
                  >
                    <option value="">Select Country</option>
                    <option value="Nigeria">Nigeria</option>
                    {options.map((c: any) => (
                      <option key={c.label} value={c.label}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    name="zip_code"
                    value={form.zip_code}
                    onChange={handleChange}
                    placeholder="Zip Code"
                    className={inputClass}
                  />
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-rose-50 text-rose-600 rounded-2xl">
                  <Heart size={24} />
                </div>
                <h2 className="text-xl font-black text-slate-800 tracking-tight">
                  Emergency Contact
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 bg-slate-50 rounded-[2rem] border border-slate-100">
                <div className="space-y-2">
                  <label className={labelClass}>Contact Name</label>
                  <input
                    type="text"
                    name="emergency_contact_name"
                    value={form.emergency_contact_name}
                    onChange={handleChange}
                    placeholder="Full Name"
                    className="w-full h-11 px-4 rounded-xl bg-white border border-slate-200 outline-none focus:border-rose-400"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className={labelClass}>Phone</label>
                  <input
                    type="tel"
                    name="emergency_contact_phone"
                    value={form.emergency_contact_phone}
                    onChange={handleChange}
                    placeholder="Phone Number"
                    className="w-full h-11 px-4 rounded-xl bg-white border border-slate-200 outline-none focus:border-rose-400"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className={labelClass}>Relationship</label>
                  <select
                    name="emergency_contact_relationship"
                    value={form.emergency_contact_relationship}
                    onChange={handleChange}
                    className="w-full h-11 px-4 rounded-xl bg-white border border-slate-200 outline-none focus:border-rose-400"
                    required
                  >
                    <option value="">Select</option>
                    <option>Spouse</option>
                    <option>Parent</option>
                    <option>Sibling</option>
                    <option>Friend</option>
                  </select>
                </div>
              </div>
            </section>
          </div>

          {/* Sidebar Image Upload */}
          <div className="lg:w-72">
            <div className="sticky top-0 space-y-6">
              <label className={labelClass}>
                Profile Picture <span className="text-red-500">*</span>
              </label>
              <div className="relative group cursor-pointer group">
                <div className="w-full aspect-[4/5] rounded-[2.5rem] bg-slate-100 border-4 border-white shadow-inner overflow-hidden flex flex-col items-center justify-center transition-all group-hover:border-blue-100 relative">
                  {profilePreview ? (
                    <img
                      src={profilePreview}
                      className="w-full h-full object-cover"
                      alt="preview"
                    />
                  ) : (
                    <div className="flex flex-col items-center text-slate-300 group-hover:text-blue-400 transition-colors">
                      <Camera size={48} strokeWidth={1.5} />
                      <span className="text-xs font-black mt-4 uppercase tracking-widest">
                        Upload Photo
                      </span>
                    </div>
                  )}
                  <input
                    type="file"
                    name="profile_picture"
                    accept={uploadAccept.image}
                    onChange={handleChange}
                    className="absolute inset-0 opacity-0 cursor-pointer z-10"
                  />

                  <div className="absolute inset-0 bg-blue-600/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <UploadCloud className="text-white" size={32} />
                  </div>
                </div>
                <div className="mt-4 p-4 bg-blue-50/50 rounded-2xl border border-blue-100">
                  <p className="text-[10px] text-blue-700 leading-relaxed font-bold uppercase">
                    <b>💡</b> Image size limit is 9MB. Formats: JPG, PNG.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between mt-12 pt-8 border-t border-slate-100 gap-4">
          <button
            type="button"
            onClick={prevStep}
            className="flex items-center gap-2 text-slate-400 font-bold hover:text-slate-600 transition-colors"
          >
            <ChevronLeft size={20} /> Back to Step 1
          </button>
          <button
            type="submit"
            disabled={loading}
            className="w-full md:w-auto px-16 h-16 bg-slate-900 text-white rounded-[1.5rem] font-black uppercase tracking-widest shadow-2xl hover:bg-black transition-all flex items-center justify-center"
          >
            {loading ? <CircleLoader /> : "Complete Registration"}
          </button>
        </div>
      </div>
    </form>
  );
}
